import React, { useState } from "react";
import { Modal, Button, Group, Stack, Text, Alert, Box, Checkbox, Select } from "@mantine/core";
import { AlertCircle } from "tabler-icons-react";

type AdminPermissionScope = 'department' | 'program' | 'user';

type AdminPermission = 'attendance.admin' | 'employees.adminLeave';

type AdminPermissionScopesModalProps = {
  opened: boolean;
  onClose: () => void;
  onConfirm: (scopes: Record<AdminPermission, AdminPermissionScope>) => void;
  enabledPermissions: AdminPermission[]; // Which admin permissions are being enabled
  existingScopes?: Record<AdminPermission, AdminPermissionScope>; // Current scopes if editing
  isLoading?: boolean;
};

const PERMISSION_LABELS: Record<AdminPermission, { label: string; description: string }> = {
  'attendance.admin': {
    label: 'Attendance Admin',
    description: 'Manage attendance for users',
  },
  'employees.adminLeave': {
    label: 'Admin Leave',
    description: 'Manage leave requests for users',
  },
};

const SCOPE_OPTIONS = [
  { value: 'department', label: 'Department', description: 'Can manage users in their department' },
  { value: 'program', label: 'Program', description: 'Can manage users in their program' },
  { value: 'user', label: 'All Affiliations', description: 'Can manage all users from departments and programs they are assigned to' },
];

export function AdminPermissionScopesModal({
  opened,
  onClose,
  onConfirm,
  enabledPermissions,
  existingScopes = {} as Record<AdminPermission, AdminPermissionScope>,
  isLoading = false,
}: AdminPermissionScopesModalProps) {
  const [scopes, setScopes] = useState<Record<AdminPermission, AdminPermissionScope>>(() => {
    const initial: Record<AdminPermission, AdminPermissionScope> = {} as Record<
      AdminPermission,
      AdminPermissionScope
    >;
    enabledPermissions.forEach((perm) => {
      initial[perm] = existingScopes[perm] || 'department';
    });
    return initial;
  });

  const handleScopeChange = (permission: AdminPermission, scope: string | null) => {
    if (scope) {
      setScopes((prev) => ({
        ...prev,
        [permission]: scope as AdminPermissionScope,
      }));
    }
  };

  const handleConfirm = () => {
    onConfirm(scopes);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Set Admin Permission Scopes"
      centered
      size="lg"
      withCloseButton={false}
    >
      <Stack spacing="lg">
        <Alert
          icon={<AlertCircle size={16} />}
          color="orange"
          title="Admin Permission Scope Assignment"
        >
          Define the scope for each admin permission. Each permission can have a different scope.
        </Alert>

        {/* Permission Scope Selections */}
        <Stack spacing="md">
          {enabledPermissions.map((permission) => (
            <Box
              key={permission}
              p="md"
              sx={{
                border: '1px solid #e9ecef',
                borderRadius: 8,
                backgroundColor: '#f9fafb',
              }}
            >
              <Group position="apart" mb="sm">
                <div>
                  <Text weight={600} size="sm">
                    {PERMISSION_LABELS[permission].label}
                  </Text>
                  <Text size="xs" color="dimmed" mt={4}>
                    {PERMISSION_LABELS[permission].description}
                  </Text>
                </div>
              </Group>

              <Select
                label="Permission Scope"
                placeholder="Select scope"
                value={scopes[permission]}
                onChange={(value) => handleScopeChange(permission, value)}
                data={SCOPE_OPTIONS.map((opt) => ({
                  value: opt.value,
                  label: `${opt.label} - ${opt.description}`,
                }))}
                searchable
                size="sm"
              />
            </Box>
          ))}
        </Stack>

        <Group position="right" spacing={20} mt="xl">
          <Button color="cancel" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} loading={isLoading}>
            Confirm
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
